import React, { ChangeEvent, useState } from "react";
import { FieldType, Unit } from "../../../services/helpers/types";
import { Input } from "@mui/material";
import { generateUnit } from "../../molecules/DoctorsReport/helpers";
import { useTranslation } from "react-i18next";

interface BMICalculatorFieldProps {
  field: FieldType;
  value?: string;
  onChange: (value?: string) => void;
}

const BMICalculatorField = (props: BMICalculatorFieldProps) => {
  const { value, onChange } = props;
  const { t } = useTranslation();

  const [weight, setWeight] = useState<string | undefined>(undefined);
  const [height, setHeight] = useState<string | undefined>(undefined); // in cm

  React.useEffect(() => {
    if (value) {
      const parts = value.split(";");
      if (parts.length >= 2) {
        setWeight(parts[0] === "" ? undefined : parts[0]);
        setHeight(parts[1] === "" ? undefined : parts[1]);
        return;
      }
    }
  }, [value]);

  const parseNumber = (v?: string) => {
    if (!v) return undefined;
    const normalized = v.replace(",", ".");
    const parsed = parseFloat(normalized);
    if (isNaN(parsed)) return undefined;
    return parsed;
  };

  const computeBMI = (w?: string, h?: string) => {
    const wNum = parseNumber(w);
    const hNum = parseNumber(h);
    if (wNum === undefined || hNum === undefined || hNum === 0) {
      onChange(`${w ?? ""};${h ?? ""}`);
      return;
    }
    const hMeters = hNum / 100;
    if (hMeters <= 0) {
      onChange(`${w ?? ""};${h ?? ""}`);
      return;
    }
    onChange(`${w ?? ""};${h ?? ""}`);
  };

  const handleWeightChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const v = event.target.value;
    setWeight(v);
    computeBMI(v, height);
  };

  const handleHeightChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const v = event.target.value;
    setHeight(v);
    computeBMI(weight, v);
  };

  return (
    <div className="dui-number-number-field dui-bmi-field">
      <div className="dui-number-field">
        <Input
          className="dui-number-field-inner"
          onChange={handleWeightChange}
          placeholder={t("weight") + " (kg)"}
          value={weight ?? ""}
          endAdornment={
            <div className="dui-number-field-unit">
              {generateUnit(t, Unit.Weight, weight)}
            </div>
          }
        />
        <Input
          className="dui-number-field-inner"
          onChange={handleHeightChange}
          placeholder={t("height") + " (cm)"}
          value={height ?? ""}
          endAdornment={
            <div className="dui-number-field-unit">
              {generateUnit(t, Unit.SizeCM, height)}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default BMICalculatorField;
